import debug from "debug";
import { assetURL } from "./config"; // Import assetURL from config
import * as THREE from "three";

const log = debug("app:texture");

/**
 * The `TextureSwitcher` class handles switching textures dynamically in a Three.js scene
 * based on different modes (e.g., hybrid, layout, cfg).
 * @category Experience
 * @subcategory World
 */
class TextureSwitcher {
  /**
   * Creates an instance of `TextureSwitcher`.
   * @param {object} experience - The experience object containing the scene, event hub, and texture vault.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = experience.scene;
    this.eventHub = experience.eventHub;
    this.currentMode = "hybrid"; // Initial mode
    this.textureVault = experience.textureVault; // Get the TextureVault instance

    this.init();
  }

  cleanup() {
    try {
      if (this.eventHub) {
        this.eventHub.off("mode-change", this.handleModeChange.bind(this));
        this.eventHub.off("textureChange", this.handleTextureChange.bind(this));
      }
  
      this.scene = null;
      this.eventHub = null;
      this.textureVault = null;
      this.currentMode = null;
  
      console.log("TextureSwitcher cleanup completed.");
    } catch (error) {
      console.error("Error during TextureSwitcher cleanup:", error);
    }
  }

  /**
   * Initializes event listeners for mode and texture changes.
   */
  init() {
    // Set up the event listener for the custom "mode-change" event
    this.eventHub.on("mode-change", this.handleModeChange.bind(this));
    this.eventHub.on("textureChange", this.handleTextureChange.bind(this));
  }

  /**
   * Handles the mode change event by updating the current mode, textures, and text visibility.
   * @param {string} event - The new mode ("hybrid", "layout", or "cfg").
   */
  handleModeChange(event) {
    log("event texture switched", event);
    // Update the current mode based on the event detail
    this.currentMode = event || "hybrid";

    log("current mode texture switched", this.currentMode);

    // Update textures for all meshes in the scene
    this.updateTextures();

    this.toggleTextVisibility();

    // Emit an event if needed
    this.eventHub.emit("textureModeChanged", this.currentMode);
  }

  /**
   * Toggles the visibility of text objects (e.g., part number and manufacturer part number)
   * based on the current mode.
   */
  toggleTextVisibility() {
    const showText = this.currentMode !== "layout";

    this.scene.traverse((object) => {
      if (object.name === "partNumber" || object.name === "MfgPart") {
        object.visible = showText;
        log(`Text geometry (${object.name}) visibility set to ${showText}`);
      }
    });
  }

  /**
   * Toggles the visibility of text objects (e.g., part number and manufacturer part number)
   * based on the current mode.
   */
  async updateTextures() {
    // Collect promises for all texture updates
    const texturePromises = [];
    log("update of the texture started");

    this.scene.traverse((object) => {
      // Check if the object has userData.textures and a child named "Shape"
      if (
        object.name === "XMO" && // Only process objects with the name "XMO"
        object.userData.mode !== "textbox" // Skip if mode is "textbox"
      ) {
        log("object", object);
        log("object.userData.textures", object.userData.textures);

        const shapeChild = object.children.find(
          (child) => child.name === "Shape"
        );
        if (shapeChild) {
          log("Preparing texture update for shape:", shapeChild.name);
          const textureKey = this.getTextureKey(object);

          if (textureKey) {
            // Prepend assetURL to the textureKey to create the full URL
            const fullTextureKey = `${assetURL}${textureKey}`;
            // Add the promise to the array
            texturePromises.push(this.setTexture(shapeChild, textureKey));
          }
        }
      }
    });

    // Wait for all textures to be updated in parallel
    await Promise.all(texturePromises);
    log("All textures updated.");
  }

  /**
   * Determines the appropriate texture key based on the current mode.
   * @param {THREE.Object3D} object - The object whose texture key needs to be determined.
   * @returns {string} - The texture key to be used.
   */
  getTextureKey(object) {
    let textureKey;
    if (this.currentMode === "hybrid") {
      log("object in hybroid mode");
      textureKey = this.getHybTextureKey(object);
    } else if (this.currentMode === "layout") {
      log("object in layout mode");
      textureKey = object.userData.textures.brd;
    } else {
      log("object in cfg mode");
      textureKey = object.userData.textures.cfg;
    }
    return textureKey;
  }

  /**
   * Handles the texture change for a specific object.
   * @param {THREE.Object3D} object - The object whose texture needs to be updated.
   * @returns {Promise<void>} - A promise that resolves after updating the texture.
   */
  async handleTextureChange(object) {
    if (!object || typeof object !== "object") {
      log("Invalid object for texture change");
      return;
    }

    const shapeChild = object.children.find((child) => child.name === "Shape");
    if (!shapeChild) {
      log("No shape child found in object:", object);
      return;
    }

    const textureKey = this.getTextureKey(object);
    log("texttureKey", textureKey);
    if (textureKey) {
      await this.setTexture(shapeChild, textureKey);
      log("Texture updated for object:", object);
    } else {
      log(
        "No valid texture key found for object in current mode:",
        this.currentMode
      );
    }
  }

  /**
   * Retrieves the appropriate hybrid mode texture key.
   * @param {THREE.Object3D} object - The object for which the hybrid texture key is required.
   * @returns {string} - The hybrid texture key.
   */
  getHybTextureKey(object) {
    const textureHybKey =
      object.userData.mode === "xm-part"
        ? object.userData.textures.hyb
        : object.userData.textures.cfg;
    log("texture key:", textureHybKey);
    return textureHybKey;
  }

  /**
   * Applies a texture to a given mesh.
   * @param {THREE.Mesh} mesh - The mesh to which the texture should be applied.
   * @param {string} textureKey - The key of the texture to be applied.
   * @returns {Promise<void>} - A promise that resolves after the texture is set.
   */
  async setTexture(mesh, textureKey) {
    try {
      if (!(mesh instanceof THREE.Mesh)) {
        console.warn(`Skipping texture application. ${mesh.name} is not a Mesh.`, mesh);
        return;
      }
  
      if (!mesh.material) {
        console.warn(`Skipping texture application. ${mesh.name} has no material.`);
        return;
      }
  
      const texture = await this.textureVault.getTexture(textureKey);
      log("texture", texture);
  
      if (texture) {
        texture.needsUpdate = true;
        mesh.material.map = texture;
        mesh.material.needsUpdate = true;
      }
    } catch (error) {
      console.error(`Failed to set texture for mesh: ${mesh.name} with key: ${textureKey}`, error);
    }
  }
  
}

export default TextureSwitcher;
